// OutletList component - Shows list of nearby fast food outlets

import React from 'react';
import { Utensils, MapPin, Star, Clock } from 'lucide-react';
import Card, { CardHeader, CardTitle, CardContent } from '../ui/Card';
import Badge from '../ui/Badge';
import { formatDistance, formatTravelTime } from '../../utils/formatters';
import { estimateTravelTime } from '../../services/distance';

const OutletList = ({ outlets = [], selectedPod, maxDistance, className = '' }) => {
  // Filter and sort outlets
  const filteredOutlets = outlets
    .filter((outlet) => !maxDistance || (outlet.distance && outlet.distance <= maxDistance))
    .sort((a, b) => (a.distance || 0) - (b.distance || 0));

  if (filteredOutlets.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Utensils className="w-5 h-5 text-secondary-500" />
            Nearby Outlets
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 text-dark-400">
            <Utensils className="w-10 h-10 mx-auto mb-2 opacity-50" />
            <p>No outlets found nearby</p>
            <p className="text-sm mt-1">
              {selectedPod
                ? 'Try expanding the search radius'
                : 'Select a POD to see nearby outlets'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Group by brand
  const groupedByBrand = filteredOutlets.reduce((acc, outlet) => {
    const brand = outlet.brand || 'Other';
    if (!acc[brand]) {
      acc[brand] = [];
    }
    acc[brand].push(outlet);
    return acc;
  }, {});

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Utensils className="w-5 h-5 text-secondary-500" />
            Nearby Outlets
          </CardTitle>
          <Badge variant="secondary">{filteredOutlets.length}</Badge>
        </div>
      </CardHeader>

      <CardContent>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {Object.entries(groupedByBrand).map(([brand, brandOutlets]) => (
            <div key={brand} className="space-y-2">
              {/* Brand Header */}
              <div className="flex items-center gap-2 pb-1 border-b border-dark-100">
                <span className="text-lg">{brandOutlets[0]?.brandIcon || '🍕'}</span>
                <span className="font-medium text-dark-700 text-sm">{brand}</span>
                <Badge variant="default" size="small">{brandOutlets.length}</Badge>
              </div>

              {/* Outlets */}
              {brandOutlets.map((outlet, index) => (
                <OutletItem
                  key={outlet.placeId || index}
                  outlet={outlet}
                />
              ))}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const OutletItem = ({ outlet }) => {
  const driveTime = outlet.distance ? estimateTravelTime(outlet.distance, 'driving') : null;
  const walkTime = outlet.distance ? estimateTravelTime(outlet.distance, 'walking') : null;

  return (
    <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-dark-50 transition-colors cursor-pointer">
      {/* Outlet Icon */}
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: outlet.brandColor || '#10B981' }}
      >
        <span className="text-white text-lg">{outlet.brandIcon || '🍕'}</span>
      </div>

      {/* Outlet Info */}
      <div className="flex-1 min-w-0">
        <p className="font-medium text-dark-900 truncate">{outlet.name}</p>
        <div className="flex items-center gap-2 text-xs text-dark-500">
          {outlet.rating && (
            <span className="flex items-center gap-1">
              <Star className="w-3 h-3 text-yellow-500" />
              {outlet.rating.toFixed(1)}
              {outlet.totalRatings && (
                <span className="text-dark-400">({outlet.totalRatings})</span>
              )}
            </span>
          )}
          {outlet.isOpen !== null && (
            <span className={outlet.isOpen ? 'text-accent-600' : 'text-red-500'}>
              {outlet.isOpen ? 'Open' : 'Closed'}
            </span>
          )}
        </div>
      </div>

      {/* Distance & Time */}
      {outlet.distance && (
        <div className="text-right flex-shrink-0">
          <p className="font-semibold text-secondary-600 text-sm">
            {formatDistance(outlet.distance)}
          </p>
          <div className="flex items-center gap-1 text-xs text-dark-500">
            <Clock className="w-3 h-3" />
            <span>{Math.round(driveTime)} min drive</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutletList;
